import {
    Add,
    ArrowForward,
    Close,
    DeleteOutline,
    FileUploadOutlined,
} from "@mui/icons-material";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Radio,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import * as AYSDialog from "components/UI/AYSDialog";
import { observer } from "mobx-react";
import type {
    GlossaryEntry,
    GlossaryEntryPayload,
} from "models/ClientBrain/GlossaryEntry";
import Papa, { type ParseError } from "papaparse";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ClientBrainStore from "stores/ClientBrainStore";
import { useStore } from "utils/useStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";

type Props = {
    store: ClientBrainStore;
    canEdit?: boolean;
};

type DraftEntry = GlossaryEntryPayload & {
    key: string;
};

type CsvCandidate = {
    name: string;
    definition: string;
    rowNumber: number;
};

type CsvColumnOption = {
    index: number;
    label: string;
};

type SkippedCsvRow = {
    rowNumber: number;
    reason: string;
};

type CsvConflict = {
    key: string;
    existing?: DraftEntry;
    uploaded: CsvCandidate[];
};

type PendingImport = {
    directEntries: CsvCandidate[];
    conflicts: CsvConflict[];
    skippedRows: SkippedCsvRow[];
};

type ImportSummary = {
    created: number;
    updated: number;
    keptExisting: number;
    skippedRows: SkippedCsvRow[];
};

type GlossaryChanges = {
    added: DraftEntry[];
    updated: Array<{
        before: GlossaryEntry;
        after: DraftEntry;
    }>;
    deleted: GlossaryEntry[];
};

const normalizeName = (name: string) => name.trim().toLocaleLowerCase();

const errorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message : fallback;

const toDraftEntries = (entries: GlossaryEntry[]): DraftEntry[] =>
    entries.map((entry) => ({ ...entry, key: entry.id }));

const getValidationErrors = (entries: DraftEntry[]) => {
    const errors: Record<string, string> = {};
    const names = new Map<string, string[]>();

    entries.forEach((entry) => {
        if (!entry.name.trim() || !entry.definition.trim()) {
            errors[entry.key] = "Term and definition are required.";
            return;
        }

        const normalized = normalizeName(entry.name);
        names.set(normalized, [...(names.get(normalized) ?? []), entry.key]);
    });

    names.forEach((keys) => {
        if (keys.length < 2) return;
        keys.forEach((key) => {
            errors[key] = "This term already exists.";
        });
    });

    return errors;
};

const getChanges = (
    savedEntries: GlossaryEntry[],
    draftEntries: DraftEntry[],
): GlossaryChanges => {
    const savedById = new Map(savedEntries.map((entry) => [entry.id, entry]));
    const draftIds = new Set(
        draftEntries.flatMap((entry) => (entry.id ? [entry.id] : [])),
    );

    return {
        added: draftEntries.filter((entry) => !entry.id),
        updated: draftEntries.flatMap((entry) => {
            if (!entry.id) return [];
            const saved = savedById.get(entry.id);
            if (
                !saved ||
                (saved.name === entry.name.trim() &&
                    saved.definition === entry.definition.trim())
            ) {
                return [];
            }
            return [{ before: saved, after: entry }];
        }),
        deleted: savedEntries.filter((entry) => !draftIds.has(entry.id)),
    };
};

const ClientGlossarySection: React.FC<Props> = observer(({
    store,
    canEdit = false,
}) => {
    const theme = useTheme();
    const messageStore = useStore(MessageStore);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const nextDraftKey = useRef(0);

    const [editing, setEditing] = useState(false);
    const [draftEntries, setDraftEntries] = useState<DraftEntry[]>([]);
    const [touchedKeys, setTouchedKeys] = useState<Set<string>>(new Set());
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [deletingKey, setDeletingKey] = useState<string>();
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [csvRows, setCsvRows] = useState<string[][]>([]);
    const [csvParseErrors, setCsvParseErrors] = useState<ParseError[]>([]);
    const [termColumn, setTermColumn] = useState<number | "">("");
    const [definitionColumn, setDefinitionColumn] = useState<number | "">("");
    const [mappingOpen, setMappingOpen] = useState(false);
    const [pendingImport, setPendingImport] = useState<PendingImport>();
    const [conflictChoices, setConflictChoices] = useState<
        Record<string, string>
    >({});
    const [importSummary, setImportSummary] = useState<ImportSummary>();

    const validationErrors = useMemo(
        () => getValidationErrors(draftEntries),
        [draftEntries],
    );
    const csvColumnOptions = useMemo<CsvColumnOption[]>(
        () =>
            csvHeaders.map((label, index) => ({
                index,
                label,
            })),
        [csvHeaders],
    );
    const changes = useMemo(
        () => getChanges(store.glossaryEntries, draftEntries),
        [draftEntries, store.glossaryEntries],
    );
    const hasChanges =
        changes.added.length > 0 ||
        changes.updated.length > 0 ||
        changes.deleted.length > 0;
    const isValid = Object.keys(validationErrors).length === 0;
    const hasIncompleteNewEntry = draftEntries.some(
        (entry) =>
            !entry.id &&
            (!entry.name.trim() || !entry.definition.trim()),
    );

    useEffect(() => {
        if (!store.isLoading) return;
        setEditing(false);
        setDraftEntries([]);
        setShowConfirm(false);
    }, [store.isLoading]);

    const resetCsvState = () => {
        setMappingOpen(false);
        setPendingImport(undefined);
        setCsvHeaders([]);
        setCsvRows([]);
        setCsvParseErrors([]);
        setTermColumn("");
        setDefinitionColumn("");
        setConflictChoices({});
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const startEdit = () => {
        setDraftEntries(toDraftEntries(store.glossaryEntries));
        setTouchedKeys(new Set());
        setShowAllErrors(false);
        setDeletingKey(undefined);
        setImportSummary(undefined);
        resetCsvState();
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraftEntries([]);
        setTouchedKeys(new Set());
        setShowAllErrors(false);
        setDeletingKey(undefined);
        setShowConfirm(false);
        setImportSummary(undefined);
        resetCsvState();
    };

    const addDraft = () => {
        if (hasIncompleteNewEntry) return;
        const key = `new-${nextDraftKey.current++}`;
        setDraftEntries((current) => [
            ...current,
            { key, name: "", definition: "" },
        ]);
    };

    const updateDraft = (
        key: string,
        field: "name" | "definition",
        value: string,
    ) => {
        setDraftEntries((current) =>
            current.map((entry) =>
                entry.key === key ? { ...entry, [field]: value } : entry,
            ),
        );
    };

    const touchDraft = (key: string) => {
        setTouchedKeys((current) => new Set(current).add(key));
    };

    const requestDelete = (entry: DraftEntry) => {
        if (!entry.id) {
            setDraftEntries((current) =>
                current.filter((item) => item.key !== entry.key),
            );
            return;
        }
        setDeletingKey(entry.key);
    };

    const confirmDelete = (key: string) => {
        setDraftEntries((current) =>
            current.filter((entry) => entry.key !== key),
        );
        setDeletingKey(undefined);
    };

    const openSaveConfirmation = () => {
        setShowAllErrors(true);
        if (!hasChanges || !isValid) return;
        setShowConfirm(true);
    };

    const confirmSave = async () => {
        setSaving(true);
        try {
            await store.saveGlossary(
                draftEntries.map(({ id, name, definition }) => ({
                    id,
                    name: name.trim(),
                    definition: definition.trim(),
                })),
            );
            setShowConfirm(false);
            setEditing(false);
            setDraftEntries([]);
            messageStore.logMessage("Client glossary saved.", "success");
        } catch (error) {
            messageStore.logError(
                errorMessage(error, "Failed to save the client glossary."),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCsvFile = (file?: File) => {
        if (!file) return;
        Papa.parse<string[]>(file, {
            skipEmptyLines: "greedy",
            complete: (result) => {
                const rows = result.data;
                if (rows.length < 2 || rows[0].length === 0) {
                    messageStore.logError(
                        "The CSV must contain a header row and at least one data row.",
                    );
                    resetCsvState();
                    return;
                }
                setCsvHeaders(
                    rows[0].map(
                        (header, index) =>
                            header.trim() || `Unnamed column ${index + 1}`,
                    ),
                );
                setCsvRows(rows.slice(1));
                setCsvParseErrors(result.errors);
                setMappingOpen(true);
            },
            error: (error) => {
                messageStore.logError(`Failed to read CSV: ${error.message}`);
                resetCsvState();
            },
        });
    };

    const prepareImport = () => {
        if (termColumn === "" || definitionColumn === "") return;

        const skippedRows: SkippedCsvRow[] = [];
        const candidates: CsvCandidate[] = [];
        const parseErrorsByRow = new Map<number, string[]>();
        csvParseErrors.forEach((error) => {
            const rowNumber = (error.row ?? 1) + 1;
            parseErrorsByRow.set(rowNumber, [
                ...(parseErrorsByRow.get(rowNumber) ?? []),
                error.message,
            ]);
        });

        csvRows.forEach((row, index) => {
            const rowNumber = index + 2;
            const parseErrors = parseErrorsByRow.get(rowNumber);
            if (parseErrors?.length) {
                skippedRows.push({
                    rowNumber,
                    reason: parseErrors.join("; "),
                });
                return;
            }

            const name = (row[termColumn] ?? "").trim();
            const definition = (row[definitionColumn] ?? "").trim();
            if (!name || !definition) {
                skippedRows.push({
                    rowNumber,
                    reason: !name
                        ? "Mapped term is blank."
                        : "Mapped definition is blank.",
                });
                return;
            }
            candidates.push({ name, definition, rowNumber });
        });

        if (candidates.length === 0) {
            resetCsvState();
            setImportSummary({
                created: 0,
                updated: 0,
                keptExisting: 0,
                skippedRows,
            });
            return;
        }

        const existingByName = new Map(
            draftEntries
                .filter((entry) => entry.name.trim())
                .map((entry) => [normalizeName(entry.name), entry]),
        );
        const grouped = new Map<string, CsvCandidate[]>();
        candidates.forEach((candidate) => {
            const key = normalizeName(candidate.name);
            grouped.set(key, [...(grouped.get(key) ?? []), candidate]);
        });

        const directEntries: CsvCandidate[] = [];
        const conflicts: CsvConflict[] = [];
        grouped.forEach((uploaded, key) => {
            const existing = existingByName.get(key);
            if (existing || uploaded.length > 1) {
                conflicts.push({ key, existing, uploaded });
            } else {
                directEntries.push(uploaded[0]);
            }
        });

        setMappingOpen(false);
        const pending = { directEntries, conflicts, skippedRows };
        if (conflicts.length === 0) {
            applyCsvToDraft(pending, {});
            return;
        }

        setPendingImport(pending);
        setConflictChoices(
            Object.fromEntries(
                conflicts
                    .filter((conflict) => conflict.existing)
                    .map((conflict) => [
                        conflict.key,
                        `existing:${conflict.existing!.key}`,
                    ]),
            ),
        );
    };

    const applyCsvToDraft = (
        pending: PendingImport,
        choices: Record<string, string>,
    ) => {
        const additions = [...pending.directEntries];
        const updates: Array<{
            key: string;
            candidate: CsvCandidate;
        }> = [];
        let keptExisting = 0;

        pending.conflicts.forEach((conflict) => {
            const choice = choices[conflict.key];
            if (choice?.startsWith("existing:")) {
                keptExisting += 1;
                return;
            }
            const selected = conflict.uploaded.find(
                (candidate) => `upload:${candidate.rowNumber}` === choice,
            );
            if (!selected) return;
            if (conflict.existing) {
                updates.push({
                    key: conflict.existing.key,
                    candidate: selected,
                });
            } else {
                additions.push(selected);
            }
        });

        setDraftEntries((current) => [
            ...current.map((entry) => {
                const update = updates.find((item) => item.key === entry.key);
                return update
                    ? {
                          ...entry,
                          name: update.candidate.name,
                          definition: update.candidate.definition,
                      }
                    : entry;
            }),
            ...additions.map((candidate) => ({
                key: `new-${nextDraftKey.current++}`,
                name: candidate.name,
                definition: candidate.definition,
            })),
        ]);
        setPendingImport(undefined);
        resetCsvState();
        setImportSummary({
            created: additions.length,
            updated: updates.length,
            keptExisting,
            skippedRows: pending.skippedRows,
        });
    };

    const editRowGridSx = {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) 24px minmax(260px, 1.5fr) 40px",
        gap: 1.5,
        alignItems: "center",
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr 24px 1fr 40px",
        },
    };
    const viewRowGridSx = {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) 24px minmax(260px, 1.5fr)",
        gap: 1.5,
        alignItems: "center",
    };
    const headerTextSx = {
        fontSize: "11px",
        fontWeight: 600,
        color: theme.palette.gray[600],
        textTransform: "uppercase",
    };

    const renderEditRow = (entry: DraftEntry) => {
        const showError = showAllErrors || touchedKeys.has(entry.key);
        const rowError = showError ? validationErrors[entry.key] : undefined;

        if (deletingKey === entry.key) {
            return (
                <Box
                    key={entry.key}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        border: `1px solid ${theme.palette.error.light}`,
                        backgroundColor: theme.palette.error[50],
                        borderRadius: "8px",
                        padding: 1.5,
                    }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.error.dark,
                            fontWeight: 600,
                        }}
                    >
                        Delete “{entry.name}”?
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <AcxButton
                            color="error"
                            startIcon={<DeleteOutline />}
                            onClick={() => confirmDelete(entry.key)}
                        >
                            Delete
                        </AcxButton>
                        <AcxButton
                            color="white"
                            onClick={() => setDeletingKey(undefined)}
                        >
                            Cancel
                        </AcxButton>
                    </Box>
                </Box>
            );
        }

        return (
            <Box key={entry.key}>
                <Box sx={editRowGridSx}>
                    <TextField
                        size="small"
                        value={entry.name}
                        placeholder="e.g. PSP"
                        error={Boolean(rowError)}
                        onChange={(event) =>
                            updateDraft(entry.key, "name", event.target.value)
                        }
                        onBlur={() => touchDraft(entry.key)}
                        autoFocus={!entry.id}
                        fullWidth
                    />
                    <ArrowForward
                        fontSize="small"
                        sx={{ color: theme.palette.gray[500] }}
                    />
                    <TextField
                        size="small"
                        value={entry.definition}
                        placeholder="e.g. Patient Support Program"
                        error={Boolean(rowError)}
                        onChange={(event) =>
                            updateDraft(
                                entry.key,
                                "definition",
                                event.target.value,
                            )
                        }
                        onBlur={() => touchDraft(entry.key)}
                        fullWidth
                    />
                    <IconButton
                        aria-label={`Remove ${entry.name || "new glossary term"}`}
                        size="small"
                        onClick={() => requestDelete(entry)}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
                {rowError && (
                    <Typography
                        sx={{
                            color: theme.palette.error.main,
                            fontSize: "12px",
                            mt: 0.5,
                        }}
                    >
                        {rowError}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <ClientBrainSectionCard
            title="Client Glossary"
            subtitle="Client-specific acronyms, team names, product codenames, and department abbreviations. KBo uses these to interpret calls and to write findings in this client's language."
            headerAction={
                canEdit &&
                !store.isLoading &&
                (editing ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <input
                            ref={fileInputRef}
                            hidden
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(event) =>
                                handleCsvFile(event.target.files?.[0])
                            }
                        />
                        <AcxButton
                            color="white"
                            variant="outlined"
                            startIcon={<FileUploadOutlined />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Upload CSV
                        </AcxButton>
                        <AcxButton
                            startIcon={<Add />}
                            disabled={hasIncompleteNewEntry}
                            onClick={addDraft}
                        >
                            Add term
                        </AcxButton>
                    </Box>
                ) : (
                    <AcxButton
                        color="primary"
                        variant="text"
                        onClick={startEdit}
                    >
                        Edit
                    </AcxButton>
                ))
            }
        >
            {store.isLoading ? (
                <Skeleton variant="rounded" height={120} />
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                        sx={{
                            ...(editing ? editRowGridSx : viewRowGridSx),
                            mb: 0.25,
                        }}
                    >
                        <Typography sx={headerTextSx}>
                            Acronym / Abbreviation
                        </Typography>
                        <Box />
                        <Typography sx={headerTextSx}>Stands for</Typography>
                        {editing && <Box />}
                    </Box>

                    {editing ? (
                        <>
                            {draftEntries.map(renderEditRow)}
                            <AcxButton
                                color="primary"
                                variant="outlined"
                                startIcon={<Add />}
                                disabled={hasIncompleteNewEntry}
                                onClick={addDraft}
                                sx={{
                                    mt: 0.25,
                                    borderStyle: "dashed",
                                    width: "100%",
                                }}
                            >
                                Add term
                            </AcxButton>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    mt: 1,
                                }}
                            >
                                <AcxButton
                                    color="white"
                                    disabled={saving}
                                    onClick={cancelEdit}
                                >
                                    Cancel
                                </AcxButton>
                                <AcxButton
                                    disabled={!hasChanges || !isValid}
                                    loading={saving}
                                    onClick={openSaveConfirmation}
                                >
                                    Save
                                </AcxButton>
                            </Box>
                        </>
                    ) : store.glossaryEntries.length > 0 ? (
                        store.glossaryEntries.map((entry) => (
                            <Box key={entry.id} sx={viewRowGridSx}>
                                <AcxMainTextField
                                    id={`client-glossary-name-${entry.id}`}
                                    value={entry.name}
                                    isDisabled
                                    fullWidth
                                />
                                <ArrowForward
                                    fontSize="small"
                                    sx={{ color: theme.palette.gray[500] }}
                                />
                                <AcxMainTextField
                                    id={`client-glossary-definition-${entry.id}`}
                                    value={entry.definition}
                                    isDisabled
                                    fullWidth
                                />
                            </Box>
                        ))
                    ) : (
                        <Typography
                            sx={{
                                color: theme.palette.gray[400],
                                fontStyle: "italic",
                                py: 1,
                            }}
                        >
                            No glossary terms added yet.
                        </Typography>
                    )}
                </Box>
            )}

            <Dialog
                open={mappingOpen}
                onClose={resetCsvState}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Map CSV columns</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <Typography color="text.secondary">
                        Choose which CSV columns contain the glossary term and its
                        definition.
                    </Typography>
                    <AcxSelectSingle<CsvColumnOption>
                        id="glossary-term-column"
                        options={csvColumnOptions}
                        valueField="index"
                        labelField="label"
                        inputLabel="Term column"
                        placeholder="Select a column…"
                        defaultValue={
                            termColumn === ""
                                ? undefined
                                : csvColumnOptions[termColumn]
                        }
                        onChange={(option) => setTermColumn(option.index)}
                        fullWidth
                    />
                    <AcxSelectSingle<CsvColumnOption>
                        id="glossary-definition-column"
                        options={csvColumnOptions}
                        valueField="index"
                        labelField="label"
                        inputLabel="Definition column"
                        placeholder="Select a column…"
                        defaultValue={
                            definitionColumn === ""
                                ? undefined
                                : csvColumnOptions[definitionColumn]
                        }
                        onChange={(option) => setDefinitionColumn(option.index)}
                        error={
                            termColumn !== "" &&
                            definitionColumn !== "" &&
                            termColumn === definitionColumn
                        }
                        helperText={
                            termColumn !== "" &&
                            definitionColumn !== "" &&
                            termColumn === definitionColumn
                                ? "Select two different columns."
                                : undefined
                        }
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <AcxButton color="white" onClick={resetCsvState}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        disabled={
                            termColumn === "" ||
                            definitionColumn === "" ||
                            termColumn === definitionColumn
                        }
                        onClick={prepareImport}
                    >
                        Continue
                    </AcxButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(pendingImport)}
                onClose={resetCsvState}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Reconcile matching terms</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <Typography color="text.secondary">
                        Choose the value to keep for each matching term.
                    </Typography>
                    {pendingImport?.conflicts.map((conflict) => (
                        <Box
                            key={conflict.key}
                            sx={{
                                border: `1px solid ${theme.palette.gray[200]}`,
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            {conflict.existing && (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "40px 1fr 1.5fr",
                                        gap: 1,
                                        alignItems: "center",
                                        padding: 1.5,
                                        backgroundColor: theme.palette.gray[50],
                                    }}
                                >
                                    <Radio
                                        checked={
                                            conflictChoices[conflict.key] ===
                                            `existing:${conflict.existing.key}`
                                        }
                                        onChange={() =>
                                            setConflictChoices((current) => ({
                                                ...current,
                                                [conflict.key]: `existing:${conflict.existing!.key}`,
                                            }))
                                        }
                                    />
                                    <Box>
                                        <Typography fontWeight={600}>
                                            {conflict.existing.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Current draft
                                        </Typography>
                                    </Box>
                                    <Typography>
                                        {conflict.existing.definition}
                                    </Typography>
                                </Box>
                            )}
                            {conflict.uploaded.map((candidate) => (
                                <Box
                                    key={candidate.rowNumber}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "40px 1fr 1.5fr",
                                        gap: 1,
                                        alignItems: "center",
                                        padding: 1.5,
                                        borderTop: `1px solid ${theme.palette.gray[200]}`,
                                    }}
                                >
                                    <Radio
                                        checked={
                                            conflictChoices[conflict.key] ===
                                            `upload:${candidate.rowNumber}`
                                        }
                                        onChange={() =>
                                            setConflictChoices((current) => ({
                                                ...current,
                                                [conflict.key]: `upload:${candidate.rowNumber}`,
                                            }))
                                        }
                                    />
                                    <Box>
                                        <Typography fontWeight={600}>
                                            {candidate.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Uploaded row {candidate.rowNumber}
                                        </Typography>
                                    </Box>
                                    <Typography>
                                        {candidate.definition}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <AcxButton color="white" onClick={resetCsvState}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        disabled={
                            !pendingImport?.conflicts.every(
                                (conflict) => conflictChoices[conflict.key],
                            )
                        }
                        onClick={() =>
                            pendingImport &&
                            applyCsvToDraft(pendingImport, conflictChoices)
                        }
                    >
                        Apply to draft
                    </AcxButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(importSummary)}
                onClose={() => setImportSummary(undefined)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>CSV applied to draft</DialogTitle>
                <DialogContent>
                    <Typography>
                        {importSummary?.created ?? 0} added to the draft,{" "}
                        {importSummary?.updated ?? 0} updated in the draft, and{" "}
                        {importSummary?.keptExisting ?? 0} current values kept.
                    </Typography>
                    {Boolean(importSummary?.skippedRows.length) && (
                        <Box sx={{ mt: 2 }}>
                            <Typography fontWeight={600}>
                                Skipped rows
                            </Typography>
                            {importSummary?.skippedRows.map((row) => (
                                <Typography
                                    key={`${row.rowNumber}-${row.reason}`}
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Row {row.rowNumber}: {row.reason}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <AcxButton onClick={() => setImportSummary(undefined)}>
                        Done
                    </AcxButton>
                </DialogActions>
            </Dialog>

            <AYSDialog.Container
                isOpen={showConfirm}
                onClose={() => !saving && setShowConfirm(false)}
                id="confirm-client-glossary-changes"
                maxWidth="md"
            >
                <AYSDialog.Title>
                    Confirm changes to Client Glossary
                </AYSDialog.Title>
                <AYSDialog.Subtitle>
                    Review the changes below before saving.
                </AYSDialog.Subtitle>
                <AYSDialog.Content>
                    <Stack direction="column" rowGap={2}>
                        {changes.added.length > 0 && (
                            <Stack direction="column" rowGap={0.75}>
                                <Typography fontWeight={600}>Added</Typography>
                                {changes.added.map((entry) => (
                                    <Typography key={entry.key} variant="body2">
                                        {entry.name.trim()} {" -> "}{" "}
                                        {entry.definition.trim()}
                                    </Typography>
                                ))}
                            </Stack>
                        )}
                        {changes.updated.length > 0 && (
                            <Stack direction="column" rowGap={1}>
                                <Typography fontWeight={600}>Updated</Typography>
                                {changes.updated.map(({ before, after }) => (
                                    <Stack
                                        key={before.id}
                                        direction="column"
                                        rowGap={0.25}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {before.name}
                                        </Typography>
                                        {before.name !== after.name.trim() && (
                                            <Typography variant="body2">
                                                Term: {before.name} {" -> "}{" "}
                                                {after.name.trim()}
                                            </Typography>
                                        )}
                                        {before.definition !==
                                            after.definition.trim() && (
                                            <Typography variant="body2">
                                                Definition: {before.definition}{" "}
                                                {" -> "}{" "}
                                                {after.definition.trim()}
                                            </Typography>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                        {changes.deleted.length > 0 && (
                            <Stack direction="column" rowGap={0.75}>
                                <Typography fontWeight={600}>Deleted</Typography>
                                {changes.deleted.map((entry) => (
                                    <Typography key={entry.id} variant="body2">
                                        {entry.name} {" -> "} {entry.definition}
                                    </Typography>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </AYSDialog.Content>
                <AYSDialog.Actions>
                    <AcxButton
                        color="white"
                        disabled={saving}
                        onClick={() => setShowConfirm(false)}
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton
                        loading={saving}
                        onClick={confirmSave}
                    >
                        Save Changes
                    </AcxButton>
                </AYSDialog.Actions>
            </AYSDialog.Container>
        </ClientBrainSectionCard>
    );
});

export default ClientGlossarySection;
